import {
  useState,
  useEffect,
  ChangeEvent,
  type PropsWithChildren,
} from "react";
import { Button } from "@/components/ui/button";
import { PersonaDocuments, updatePersona, uploadAvatar } from "@/api/api.ts";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Container } from "@/components/Container";
import {
  Persona,
  useDeletePersonaDetailsMutation,
  usePersonaWithDocumentsQuery,
} from "@/api/personas.ts";
import { useQueryClient } from "@tanstack/react-query";
import { PersonaDetailsForm } from "@/components/manage-personas/PersonaDetailsForm.tsx";
import { useToast } from "@/hooks/use-toast.ts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { H4 } from "@/components/ui/h4.tsx";
import { ChevronLeftIcon } from "lucide-react";
import { LoadingButton } from "@/components/LoadingButton";
import { EditPersonaDetailsForm } from "@/components/manage-personas/EditPersonaDetailsForm";

export function UpdatePersonaDetails() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { personaId } = useParams();

  const [saveLoading, setSaveLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const { mutate: performActionOnPersonaDetails } =
    useDeletePersonaDetailsMutation();
  const personaWithDocumentsQuery = usePersonaWithDocumentsQuery(
    personaId ?? "",
  );

  const personaData: Persona = personaWithDocumentsQuery.data?.persona || {
    persona_id: "",
    name: "",
    gender: "male",
    twitter: "",
    avatar_id: "",
  };

  const documents: PersonaDocuments = personaWithDocumentsQuery.data
    ?.documents || {
    static_web_links: [],
    dynamic_web_links: [],
    google_drive_folders: [],
    github_repos: [],
    knowledge_graph_status: "",
    last_built: null,
    core_personality: [],
    static_data: [],
    live_data: [],
    tools: [],
  };
  const queryClient = useQueryClient();

  useEffect(() => {
    if (personaWithDocumentsQuery.isError) {
      toast({ description: "Error loading agent or documents" });
    }
  }, [personaWithDocumentsQuery.isError]);

  const handleFileChange = async (
    personaId: string,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      try {
        setSaveLoading(true);
        // Upload avatar based on the persona_id entered in the form
        const uploadResponse = await uploadAvatar(selectedFile);
        const avatarId = uploadResponse.file;

        await updatePersona(
          personaId,
          { ...personaData, avatar_id: avatarId },
          documents,
          uploadResponse.file_path,
        );

        await queryClient.invalidateQueries({ queryKey: ["personas"] });

        toast({ description: "Avatar uploaded successfully" });

        return avatarId;
      } catch (error) {
        console.error("Error uploading avatar:", error);
        toast({ description: "Error uploading avatar" });
      } finally {
        setSaveLoading(false);
      }
    }
  };

  const handleDelete = async () =>
    new Promise<void>((resolve, reject) => {
      setArchiveLoading(true);
      performActionOnPersonaDetails(personaData.persona_id, {
        onSuccess: () => {
          toast({ description: "Agent archived successfully" });
          navigate("/agents");
          setArchiveLoading(false);
          resolve();
        },
        onError: (error) => {
          console.error("Error deleting agent:", error);
          toast({ description: "Error deleting agent." });
          setArchiveLoading(false);
          reject(error);
        },
      });
    });

  const handleSave = async (
    personaData: Persona,
    documents: PersonaDocuments,
  ) => {
    if (!personaId) return;

    try {
      setSaveLoading(true);
      await updatePersona(personaId, personaData, documents);
      await queryClient.invalidateQueries({ queryKey: ["personas"] });
      toast({ description: "Agent updated." });
    } catch (error) {
      console.error("Error saving agent:", error);
      toast({ description: "Error saving agent" });

      // It is important to rethrow the error here so that the error is caught by its caller
      throw error;
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Container>
      <div className="relative w-full flex flex-row justify-center items-center">
        <Link
          className="absolute left-0"
          to={`/agents/${personaData.persona_id}`}
        >
          <Button
            className="hidden sm:flex flex-row gap-x-2 text-primary"
            variant="ghost"
            color="primary"
          >
            <ChevronLeftIcon /> Back
          </Button>
        </Link>
        <H4 className="text-primary text-center font-medium py-4">{`Edit ${personaData.name || ""}`}</H4>
      </div>

      <PersonaDetailsForm
        key={personaData.persona_id}
        persona={personaData}
        documents={documents}
        onFileChange={handleFileChange}
        onSave={handleSave}
        showHeaderOptions={true}
      >
        <EditPersonaDetailsForm
          personaData={personaData}
          documents={documents}
          onSave={handleSave}
          onCancel={() => navigate(`/agents/${personaData.persona_id}`)}
          loading={saveLoading}
        >
          {personaData.persona_id && (
            <ArchiveAlertDialog
              personaName={personaData.name}
              onArchive={handleDelete}
              archiveLoading={archiveLoading}
            >
              <AlertDialogTrigger asChild>
                <LoadingButton
                  className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  variant="ghost"
                  loading={archiveLoading}
                  disabled={saveLoading}
                >
                  Archive Agent
                </LoadingButton>
              </AlertDialogTrigger>
            </ArchiveAlertDialog>
          )}
        </EditPersonaDetailsForm>
      </PersonaDetailsForm>
    </Container>
  );
}

function ArchiveAlertDialog({
  personaName,
  children,
  onArchive,
  archiveLoading,
}: PropsWithChildren<{
  personaName: string;
  onArchive: () => Promise<void>;
  archiveLoading: boolean;
}>) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const isLoading = loading || archiveLoading;

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await onArchive();
      setOpen(false);
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {children}
      <AlertDialogContent
        /* Do not close alert on escape key press when loading */
        onEscapeKeyDown={isLoading ? (e) => e.preventDefault() : undefined}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Archive Agent</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="flex flex-col items-center">
          <p>
            Are you sure you want to archive the agent &quot;{personaName}
            &quot;?
          </p>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild variant="destructive">
            <LoadingButton
              loading={isLoading}
              onClick={(e) => {
                // Prevents the dialog from closing
                e.preventDefault();
                handleConfirmDelete();
              }}
            >
              Confirm Archive
            </LoadingButton>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
